import React, { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import PaymentChoice from '../payment/PaymentChoice';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';

const client = generateClient({ authMode: 'apiKey' });

interface ResumePaymentPageProps {
  memberId: string;
  token: string;
}

interface ResumePaymentDetails {
  feeKey: string;
  amountCents: number;
  payerName: string;
  payerEmail: string;
  entityId: string;
}

/** Landing page for an admin-issued, unauthenticated payment-recovery link. */
const ResumePaymentPage: React.FC<ResumePaymentPageProps> = ({ memberId, token }) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<ResumePaymentDetails | null>(null);
  const [paymentDone, setPaymentDone] = useState(false);
  const [contactedClub, setContactedClub] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!memberId || !token) throw new Error('Missing recovery credentials');
        const res: any = await (client.queries as any).resumePaymentLookup({ memberId, token });
        const data = parseLambdaResponse<{ found?: boolean } & Partial<ResumePaymentDetails>>(
          res?.data
        );
        if (
          !cancelled &&
          data?.found &&
          data.feeKey &&
          typeof data.amountCents === 'number' &&
          data.payerName &&
          data.payerEmail &&
          data.entityId === memberId
        ) {
          setDetails({
            feeKey: data.feeKey,
            amountCents: data.amountCents,
            payerName: data.payerName,
            payerEmail: data.payerEmail,
            entityId: data.entityId,
          });
        } else if (!cancelled) {
          setDetails(null);
        }
      } catch (err) {
        console.error('Error resolving resume-payment link:', err);
        if (!cancelled) setDetails(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [memberId, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-12 px-4 flex items-center justify-center">
        <div className="text-gray-600">Loading…</div>
      </div>
    );
  }

  if (paymentDone) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-12 px-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">🎣</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Complete!</h1>
          <p className="text-gray-600">
            Thanks — your membership payment has been received. We'll be in touch once your
            application is approved.
          </p>
        </div>
      </div>
    );
  }

  if (contactedClub) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-12 px-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">No worries!</h1>
          <p className="text-gray-600">
            Please contact us at{' '}
            <a href="mailto:fishing@tumbyfc.com" className="text-fishing-blue underline">
              fishing@tumbyfc.com
            </a>{' '}
            to arrange payment on the day.
          </p>
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-12 px-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            This payment link is no longer valid
          </h1>
          <p className="text-gray-600">
            Please contact the club to have it reissued:{' '}
            <a href="mailto:fishing@tumbyfc.com" className="text-fishing-blue underline">
              fishing@tumbyfc.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  const amountDisplay = `$${(details.amountCents / 100).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-12 px-4">
      <div className="max-w-md w-full mx-auto bg-white rounded-lg shadow-lg p-8">
        <img
          src="/tumbulgum-logo.png"
          alt="Tumbulgum Fishing Club"
          className="h-16 w-auto mx-auto mb-6"
        />
        <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">
          Complete Your Membership Payment
        </h1>
        <p className="text-sm text-gray-600 mb-6 text-center">
          Hi {details.payerName}, choose how you'd like to pay your membership fee:
        </p>

        <PaymentChoice
          amountCents={details.amountCents}
          amountDisplay={amountDisplay}
          paymentType="MEMBERSHIP_NEW"
          entityId={details.entityId}
          entityType="MEMBER"
          payerEmail={details.payerEmail}
          payerName={details.payerName}
          feeKey={details.feeKey}
          authMode="apiKey"
          resumePaymentToken={token}
          onSuccess={() => setPaymentDone(true)}
          onPayOnDay={() => setContactedClub(true)}
        />
      </div>
    </div>
  );
};

export default ResumePaymentPage;
